import React from 'react';
import {
  TextField,
  Grid,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

/**
 * Component for deductions input section
 */
function DeductionsSection({ 
  deductions, 
  onDeductionChange,
  onCustomDeductionChange,
  onAddCustomDeduction,
  onRemoveCustomDeduction,
  onLoanBalanceChange,
  onAddLoanBalance,
  onRemoveLoanBalance,
  onLeaveBalanceChange,
  onAddLeaveBalance,
  onRemoveLeaveBalance,
  focusedField,
  onFocus,
  onBlur,
  getDisplayValue
}) {
  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#1e5d45" }}>
          Deductions
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label="Total Deducted Hours"
          type="text"
          variant="outlined"
          fullWidth
          size="small"
          value={deductions.totalDeductedHours}
          onChange={(e) => onDeductionChange('totalDeductedHours', e.target.value)}
          placeholder="0.00"
          helperText="Hours to deduct from total"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label="Deducted Hours Multiplier"
          type="text"
          variant="outlined"
          fullWidth
          size="small"
          value={deductions.totalDeductedHoursMultiplier}
          onChange={(e) => onDeductionChange('totalDeductedHoursMultiplier', e.target.value)}
          placeholder="1.0"
          helperText="Default: 1.0x"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label="SSS"
          type="text"
          variant="outlined"
          fullWidth
          size="small"
          value={getDisplayValue(focusedField, 'sss', deductions.sss)}
          onChange={(e) => onDeductionChange('sss', e.target.value)}
          onFocus={() => onFocus('sss')}
          onBlur={() => onBlur()}
          InputProps={{
            startAdornment: <InputAdornment position="start">PHP</InputAdornment>,
          }}
          placeholder="0.00"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label="SSS Provident"
          type="text"
          variant="outlined"
          fullWidth
          size="small"
          value={getDisplayValue(focusedField, 'sssProvident', deductions.sssProvident)}
          onChange={(e) => onDeductionChange('sssProvident', e.target.value)}
          onFocus={() => onFocus('sssProvident')}
          onBlur={() => onBlur()}
          InputProps={{
            startAdornment: <InputAdornment position="start">PHP</InputAdornment>,
          }}
          placeholder="0.00"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label="PhilHealth"
          type="text"
          variant="outlined"
          fullWidth
          size="small"
          value={getDisplayValue(focusedField, 'philhealth', deductions.philhealth)}
          onChange={(e) => onDeductionChange('philhealth', e.target.value)}
          onFocus={() => onFocus('philhealth')}
          onBlur={() => onBlur()}
          InputProps={{
            startAdornment: <InputAdornment position="start">PHP</InputAdornment>,
          }}
          placeholder="0.00"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label="Pag-IBIG"
          type="text"
          variant="outlined"
          fullWidth
          size="small"
          value={getDisplayValue(focusedField, 'pagIbig', deductions.pagIbig)}
          onChange={(e) => onDeductionChange('pagIbig', e.target.value)}
          onFocus={() => onFocus('pagIbig')}
          onBlur={() => onBlur()}
          InputProps={{
            startAdornment: <InputAdornment position="start">PHP</InputAdornment>,
          }}
          placeholder="0.00"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label="Union Dues"
          type="text"
          variant="outlined"
          fullWidth
          size="small"
          value={getDisplayValue(focusedField, 'unionDues', deductions.unionDues)}
          onChange={(e) => onDeductionChange('unionDues', e.target.value)}
          onFocus={() => onFocus('unionDues')}
          onBlur={() => onBlur()}
          InputProps={{
            startAdornment: <InputAdornment position="start">PHP</InputAdornment>,
          }}
          placeholder="0.00"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label="Tax"
          type="text"
          variant="outlined"
          fullWidth
          size="small"
          value={getDisplayValue(focusedField, 'tax', deductions.tax)}
          onChange={(e) => onDeductionChange('tax', e.target.value)}
          onFocus={() => onFocus('tax')}
          onBlur={() => onBlur()}
          InputProps={{
            startAdornment: <InputAdornment position="start">PHP</InputAdornment>,
          }}
          placeholder="0.00"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <TextField
          label="Pag-IBIG 2"
          type="text"
          variant="outlined"
          fullWidth
          size="small"
          value={getDisplayValue(focusedField, 'pagIbig2', deductions.pagIbig2)}
          onChange={(e) => onDeductionChange('pagIbig2', e.target.value)}
          onFocus={() => onFocus('pagIbig2')}
          onBlur={() => onBlur()}
          InputProps={{
            startAdornment: <InputAdornment position="start">PHP</InputAdornment>,
          }}
          placeholder="0.00"
        />
      </Grid>

      {/* Custom Deduction Entries */}
      {deductions.customDeductions?.map((deduction, index) => (
        <Grid item xs={12} sm={6} md={4} key={`custom-deduction-${index}`}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'start' }}>
            <TextField
              label="Deduction Label"
              type="text"
              variant="outlined"
              fullWidth
              size="small"
              value={deduction.label}
              onChange={(e) => onCustomDeductionChange(index, 'label', e.target.value)}
            />
            <TextField
              label="Amount"
              type="text"
              variant="outlined"
              size="small"
              value={getDisplayValue(focusedField, `customDeduction-${index}`, deduction.amount)}
              onChange={(e) => onCustomDeductionChange(index, 'amount', e.target.value)}
              onFocus={() => onFocus(`customDeduction-${index}`)}
              onBlur={() => onBlur()}
              InputProps={{
                startAdornment: <InputAdornment position="start">PHP</InputAdornment>,
              }}
              sx={{ width: '180px' }}
            />
            <IconButton
              onClick={() => onRemoveCustomDeduction(index)}
              color="error"
              size="small"
            >
              <RemoveIcon />
            </IconButton>
          </Box>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAddCustomDeduction}
          size="small"
        >
          Add Custom Deduction
        </Button>
      </Grid>

      {/* Loan Balances Section */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#1e5d45", mt: 2 }}>
          Loan Balances (For Reference Only)
        </Typography>
      </Grid>

      {deductions.loanBalances?.map((loan, index) => (
        <Grid item xs={12} sm={6} md={4} key={`loan-balance-${index}`}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'start' }}>
            <TextField
              label="Loan Type"
              type="text"
              variant="outlined"
              fullWidth
              size="small"
              value={loan.label}
              onChange={(e) => onLoanBalanceChange(index, 'label', e.target.value)}
            />
            <TextField
              label="Balance"
              type="text"
              variant="outlined"
              size="small"
              value={loan.value}
              onChange={(e) => onLoanBalanceChange(index, 'value', e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">PHP</InputAdornment>,
              }}
              sx={{ width: '180px' }}
            />
            <IconButton
              onClick={() => onRemoveLoanBalance(index)}
              color="error"
              size="small"
            >
              <RemoveIcon />
            </IconButton>
          </Box>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAddLoanBalance}
          size="small"
        >
          Add Loan Balance
        </Button>
      </Grid>

      {/* Leave Balances Section */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#1e5d45", mt: 2 }}>
          Leave Balances (For Reference Only)
        </Typography>
      </Grid>

      {deductions.leaveBalances?.map((leave, index) => (
        <Grid item xs={12} sm={6} md={4} key={`leave-balance-${index}`}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'start' }}>
            <TextField
              label="Leave Type"
              type="text"
              variant="outlined"
              fullWidth
              size="small"
              value={leave.label}
              onChange={(e) => onLeaveBalanceChange(index, 'label', e.target.value)}
            />
            <TextField
              label="Days"
              type="text"
              variant="outlined"
              size="small"
              value={leave.value}
              onChange={(e) => onLeaveBalanceChange(index, 'value', e.target.value)}
              sx={{ width: '120px' }}
            />
            <IconButton
              onClick={() => onRemoveLeaveBalance(index)}
              color="error"
              size="small"
            >
              <RemoveIcon />
            </IconButton>
          </Box>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAddLeaveBalance}
          size="small"
        >
          Add Leave Balance
        </Button>
      </Grid>
    </>
  );
}

export default DeductionsSection;
